import { useEffect } from "react";
import { ArrowUpRight, Mail, Phone } from "lucide-react";
import { CONTACT } from "@/data/content";
import { MaskLine, Reveal, SectionLabel } from "@/components/site/Reveal";
import { WhatsAppIcon } from "@/components/site/WhatsAppButton";
import { ContactForm } from "@/components/site/ContactForm";

const CHANNELS = [
  { key: "email", label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, note: "For detailed enquiries and proposals", Icon: Mail },
  { key: "phone", label: "Phone", value: CONTACT.phone, href: CONTACT.phoneHref, note: "Call during business hours (IST)", Icon: Phone },
  { key: "whatsapp", label: "WhatsApp", value: CONTACT.phone, href: CONTACT.whatsappHref, note: "Send a message directly", Icon: WhatsAppIcon, external: true },
];

const ChannelCard = ({ c, i }) => (
  <Reveal delay={i * 0.1}>
    <a
      href={c.href}
      {...(c.external ? { target: "_blank", rel: "noreferrer" } : {})}
      className="group relative flex h-full flex-col bg-[#F6F2E8] border border-[#D8CFBC] p-8 sm:p-10 overflow-hidden"
      data-testid={`contact-page-${c.key}-card`}
    >
      <span className="absolute inset-0 bg-[#16302B] translate-y-full transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-y-0" />
      <span className="relative flex items-center justify-between">
        <span className="flex h-11 w-11 items-center justify-center border border-[#B8874A] text-[#B8874A]">
          <c.Icon className="h-5 w-5" strokeWidth={1.5} />
        </span>
        <ArrowUpRight className="h-5 w-5 text-[#B8874A] transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.25} />
      </span>
      <span className="relative eyebrow mt-10">{c.label}</span>
      <span className="relative font-display text-2xl sm:text-[1.7rem] mt-3 text-[#221F1B] break-words transition-colors duration-500 group-hover:text-white">{c.value}</span>
      <span className="relative mt-3 text-[14px] text-[#5C564E] transition-colors duration-500 group-hover:text-white/70">{c.note}</span>
    </a>
  </Reveal>
);

export default function ContactPage() {
  useEffect(() => {
    document.title = "Contact — Scale Solutions by Sadiya";
  }, []);

  return (
    <main className="bg-[#F6F2E8] min-h-screen pt-[72px]" data-testid="contact-page">
      <section className="max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-12 pt-20 sm:pt-28 pb-16">
        <SectionLabel index="—">Contact</SectionLabel>
        <h1 className="font-display mt-8 text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-[-0.02em] text-[#221F1B]" data-testid="contact-page-headline">
          <MaskLine>Ready to move from</MaskLine>
          <MaskLine delay={0.12} className="italic text-[#16302B]">chaos to clarity?</MaskLine>
        </h1>
        <p className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-[#5C564E]">
          Let's start with a Discovery Session — a focused conversation to understand your goals and map the right path forward.
        </p>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
          {CHANNELS.map((c, i) => <ChannelCard key={c.key} c={c} i={i} />)}
        </div>
      </section>
      <section className="relative bg-[#16302B] text-white py-20 sm:py-28 grain overflow-hidden" data-testid="contact-page-form-section">
        <div className="absolute inset-0 hairline-grid pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <p className="eyebrow">Book a Discovery Session</p>
          <h2 className="font-display mt-5 text-3xl sm:text-4xl">Prefer to write? <span className="italic text-[#B8874A]">Send a note.</span></h2>
          <div className="mt-12"><ContactForm /></div>
        </div>
      </section>
    </main>
  );
}
